import { LEGO_CONSTANTS } from 'constants/index';
import { formatISO, startOfDay, addDays, getDay, startOfMonth, lastDayOfMonth } from 'date-fns';

// date-fns can format the `A` (AM/PM) token but cannot parse it, which makes fw-datepicker
// reject the initial value of a date-time field. `a` works for both formatting and parsing.
const normalizeTimeFormat = timeFormat => timeFormat.replace(/A/g, 'a');

// Resolves host-provided date-fns formats, tolerating null/legacy-string/partial payloads.
export function resolveDateFormats(dateTimeFormat) {
  const f = dateTimeFormat && typeof dateTimeFormat === 'object' ? dateTimeFormat : {};
  return {
    date: f.date || LEGO_CONSTANTS.DEFAULT_DATE_FORMAT,
    time: normalizeTimeFormat(f.time || LEGO_CONSTANTS.DEFAULT_TIME_FORMAT),
  };
}

const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(yesterday.getDate() - 1);

export function format(date) {
  return formatISO(date, { representation: 'date' });
}

function getFormattedDate(date) {
  return format(startOfDay(date));
}

function getPreviousDate(days, date = new Date()) {
  const yesterday = new Date(date);
  yesterday.setDate(yesterday.getDate() - days);
  return yesterday;
}

function getNextDate(days, date = new Date()) {
  return addDays(date, days);
}

function getWeek(date) {
  const today = getDay(date);
  const from = getFormattedDate(getPreviousDate(today, date));
  const to = getFormattedDate(getNextDate(6 - today, date));
  return { from, to };
}

function getMonth(date) {
  const from = format(startOfMonth(date));
  const to = format(lastDayOfMonth(date));
  return { from, to };
}

export function getToday() {
  const today = new Date();
  return getFormattedDate(today);
}

export function getYesterday() {
  const yesterday = getPreviousDate(1);
  return getFormattedDate(yesterday);
}

export function getTomorrow() {
  const tomorrow = getNextDate(1);
  return getFormattedDate(tomorrow);
}

export function getCurrentWeek() {
  return getWeek(new Date());
}

export function getLastWeek() {
  return getWeek(getPreviousDate(7));
}

export function getCurrentMonth() {
  return getMonth(new Date());
}

export function getLastMonth() {
  const date = new Date();
  const lastMonthDate = new Date(date.getFullYear(), date.getMonth(), 0);
  return getMonth(lastMonthDate);
}

// The host sends the user's timezone as a legacy abbreviation (e.g. 'IST') rather than an IANA name. Chrome accepts a few
// of those in Intl, but Safari and Firefox throw `RangeError: invalid time zone`. Where an abbreviation is ambiguous
// (IST is India, Ireland and Israel; BST/CST also clash) the zone the product's hosts actually send is used.
const LEGACY_TIME_ZONE_ABBREVIATIONS = {
  IST: 'Asia/Kolkata',
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  MDT: 'America/Denver',
  CDT: 'America/Chicago',
  EDT: 'America/New_York',
  BST: 'Europe/London',
  CEST: 'Europe/Paris',
  EEST: 'Europe/Athens',
  MSK: 'Europe/Moscow',
  GST: 'Asia/Dubai',
  SGT: 'Asia/Singapore',
  HKT: 'Asia/Hong_Kong',
  JST: 'Asia/Tokyo',
  KST: 'Asia/Seoul',
  AEST: 'Australia/Sydney',
  AEDT: 'Australia/Sydney',
  NZST: 'Pacific/Auckland',
  NZDT: 'Pacific/Auckland',
  SAST: 'Africa/Johannesburg',
  WAT: 'Africa/Lagos',
  EAT: 'Africa/Nairobi',
};

const isValidTimeZone = timeZone => {
  try {
    return !!new Intl.DateTimeFormat('en-US', { timeZone });
  } catch (error) {
    return false;
  }
};

const normalizedTimeZones = new Map();

/**
 * Returns a timezone every browser's Intl accepts for the given host-supplied value, or `undefined` (= use the browser's
 * own timezone) when it can't be resolved - it never throws. Valid IANA names are returned as they are, known legacy
 * abbreviations (see LEGACY_TIME_ZONE_ABBREVIATIONS) are mapped to their IANA name.
 * @param {string} [timeZone]
 * @returns {string|undefined}
 */
export function normalizeTimeZone(timeZone) {
  if (!timeZone || typeof timeZone !== 'string') return undefined;
  if (normalizedTimeZones.has(timeZone)) return normalizedTimeZones.get(timeZone);

  // The abbreviation table goes first so every browser resolves e.g. 'IST' the same way (some engines accept it, with their own guess).
  const mapped = LEGACY_TIME_ZONE_ABBREVIATIONS[timeZone.toUpperCase()];
  let resolved;
  if (mapped && isValidTimeZone(mapped)) {
    resolved = mapped;
  } else if (isValidTimeZone(timeZone)) {
    resolved = timeZone;
  } else {
    console.error(`Unsupported timezone "${timeZone}", falling back to the browser timezone`);
  }
  normalizedTimeZones.set(timeZone, resolved);
  return resolved;
}

/**
 * Serializes a datetime value to ISO 8601 UTC string (YYYY-MM-DDTHH:mm:ss.sssZ).
 * Used for DATE_TIME form fields to match backend format.
 * When only a date is provided (no time), defaults to midnight (00:00:00) UTC.
 * @param {string|number|Date} value - Value to serialize
 * @param {string} [timeZone] - IANA timezone name (e.g. 'America/New_York')
 * @returns {string|*} ISO string or original value if invalid/empty
 */
export function serializeDateTimeToISO(value, timeZone) {
  if (!value) return value;
  // Already an ISO string, return as-is
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) {
    return value;
  }

  let date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  // If input is a date string in YYYY-MM-DD format (date-only),
  // parse it as UTC midnight to avoid timezone offset issues
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
  }

  return date.toISOString();
}

/**
 * Returns a Date whose local getters (getFullYear/getHours/etc.) reflect the wall-clock time in `timeZone`
 * for the given UTC-based value. Used to *display* a stored UTC value as the target timezone's local time
 * (e.g. in a date-time picker that only understands the browser's own local time).
 * Native Intl-based - no extra dependency, matches the `timeZone` option already used by CommonUtils.timeSince.
 * @param {string|number|Date} value
 * @param {string} [timeZone] - IANA timezone name (e.g. 'America/New_York')
 * @returns {Date}
 */
export function utcToZonedTime(value, timeZone) {
  const date = new Date(value);
  const resolvedTimeZone = normalizeTimeZone(timeZone);
  if (!resolvedTimeZone || Number.isNaN(date.getTime())) return date;
  return new Date(date.toLocaleString('en-US', { timeZone: resolvedTimeZone }));
}

/**
 * Returns how far ahead of UTC `timeZone`'s wall-clock is, in milliseconds, at the given instant (e.g. -300*60000
 * for EST/UTC-5). Uses Intl.DateTimeFormat.formatToParts to read `timeZone`'s wall-clock components directly,
 * rather than round-tripping through a locale string re-parsed as the *runtime's own* local time (a common
 * pitfall - re-parsing that way silently produces the wrong offset whenever the runtime's own timezone isn't UTC).
 * @param {Date} date
 * @param {string} timeZone - IANA timezone name
 * @returns {number}
 */
function getTimeZoneOffsetMs(date, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(date).reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
  const asUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  return asUTC - date.getTime();
}

/**
 * Inverse of utcToZonedTime: given a value whose local getters represent a wall-clock time intended to be in
 * `timeZone`, returns the equivalent UTC instant.
 * @param {string|number|Date} value
 * @param {string} [timeZone] - IANA timezone name (e.g. 'America/New_York')
 * @returns {Date}
 */
export function zonedTimeToUtc(value, timeZone) {
  const date = new Date(value);
  const resolvedTimeZone = normalizeTimeZone(timeZone);
  if (!resolvedTimeZone || Number.isNaN(date.getTime())) return date;
  // Treat the input's local getters as if they were already UTC, as an anchor instant close enough to the
  // real one to compute timeZone's offset at that moment (accurate except right at a DST transition edge).
  const asUTC = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
  const offsetMs = getTimeZoneOffsetMs(asUTC, resolvedTimeZone);
  return new Date(asUTC.getTime() - offsetMs);
}

const MOMENT_TO_DATE_FNS_TOKENS = [
  ['YYYY', 'yyyy'],
  ['YY', 'yy'],
  ['DD', 'dd'],
  ['D', 'd'],
  ['A', 'a'],
];

/**
 * Translates a moment.js-style format token string (e.g. 'MM/DD/YYYY') into the equivalent date-fns v2 token
 * string, since crayons' fw-datepicker displayFormat/timeFormat props expect date-fns tokens. MM/M/HH/H/hh/h/
 * mm/m/ss/s are identical between the two conventions, so only the differing tokens are substituted, longest
 * first so YYYY isn't partially clobbered by a YY replacement.
 * @param {string} momentFormat
 * @returns {string}
 */
export function convertMomentFormatToDateFns(momentFormat) {
  if (!momentFormat) return momentFormat;
  return MOMENT_TO_DATE_FNS_TOKENS.reduce((result, [momentToken, dateFnsToken]) => result.split(momentToken).join(dateFnsToken), momentFormat);
}

/**
 * Splits a combined moment.js-style dateTimeFormat (e.g. 'MM/DD/YYYY hh:mm A', as sent by the host in
 * data.user.locale.dateTimeFormat) into separate date-fns format strings for a date-time picker's
 * displayFormat/timeFormat props. Assumes the date and time portions are separated by whitespace.
 * @param {string} dateTimeFormat
 * @returns {{datePart: string|null, timePart: string|null}}
 */
export function splitDateTimeFormat(dateTimeFormat) {
  if (!dateTimeFormat) return { datePart: null, timePart: null };
  const dateSegments = [];
  const timeSegments = [];
  dateTimeFormat
    .trim()
    .split(/\s+/)
    .forEach(segment => {
      const isTimeSegment = /[Hhms]/.test(segment) || segment === 'A' || segment === 'a';
      (isTimeSegment ? timeSegments : dateSegments).push(segment);
    });
  return {
    datePart: dateSegments.length ? convertMomentFormatToDateFns(dateSegments.join(' ')) : null,
    timePart: timeSegments.length ? convertMomentFormatToDateFns(timeSegments.join(' ')) : null,
  };
}
